import { FormEvent, useState } from "react";
import { useShownColorScheme } from "hooks/useShownColorScheme";
import { faSave } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ActionIcon,
  Button,
  Center,
  Loader,
  Popover,
  Stack,
  TextInput,
  useMantineTheme,
} from "@mantine/core";
import { Tooltip } from "components/UI/Tooltip";
import { useAuth } from "stores/useAuth";
import { useGenericModals } from "hooks/useGenericModals";
import { useUserTracks } from "stores/useUserTracks";
import { useTranslation } from "react-i18next";
import { getNewTrackData } from "stores/routingStore/getTrackData";
import { useRouting } from "stores/routingStore/RoutingContext";
import { getErrorMsg } from "utils/getErrorMsg";
import classes from "./SaveIndicatorIcon.module.css";

export const SaveIndicatorIcon = () => {
  const { user } = useAuth();
  const [isSavePopoverOpen, setIsSavePopoverOpen] = useState(false);
  const { selectedTrack, createTrack, trackStatus } = useUserTracks();
  const [newTrackName, setNewTrackName] = useState("");
  const { state, openedTrack } = useRouting();
  const { trackWaypoints, isRouting, areElevationsLoading } = state;
  const { openErrorModal } = useGenericModals();
  const { t } = useTranslation();
  const haveSaveableTrackwaypoints = trackWaypoints.length > 1;
  // Saved before its route or elevations are loaded, the new track would have the old ones
  const isTrackLoading = isRouting || areElevationsLoading;
  const canSaveAsNewTrack =
    !!user && haveSaveableTrackwaypoints && !isTrackLoading;
  const theme = useMantineTheme();
  const colorScheme = useShownColorScheme();

  const handleNewTrackSave = async () => {
    try {
      await createTrack(
        getNewTrackData(
          state,
          openedTrack,
          newTrackName.length > 0 ? newTrackName : "Track"
        )
      );
    } catch (error) {
      const msg = getErrorMsg(error);
      openErrorModal({ title: t("generic.error_title"), text: msg ?? "" });
    }
  };

  const handleSaveTrackSubmit = async (e: FormEvent) => {
    e.preventDefault();
    // The route may have started loading again while the popover was open
    if (!canSaveAsNewTrack) return;
    setIsSavePopoverOpen(false);
    await handleNewTrackSave();
    setNewTrackName("");
  };

  const getSaveTrackButtonLabel = () => {
    if (!user)
      return t("editmode.save_indicator.tooltips.login_to_enable_saving");
    if (trackStatus === "saving")
      return t("editmode.save_indicator.tooltips.saving_track");
    if (!haveSaveableTrackwaypoints)
      return t("editmode.save_indicator.tooltips.not_enough_trackpoints");
    // An own track is saved automatically after each change
    if (selectedTrack && trackStatus === "saved")
      return t("editmode.save_indicator.tooltips.saved");
    if (isTrackLoading)
      return t("editmode.save_indicator.tooltips.wait_for_track");
    return t("editmode.save_indicator.tooltips.save");
  };

  return (
    <Popover
      opened={isSavePopoverOpen}
      onClose={() => setIsSavePopoverOpen(false)}
      position="bottom"
      transitionProps={{
        transition: "pop-top-left",
      }}
      styles={{ dropdown: { border: "none" } }}
      withinPortal
      trapFocus
    >
      <Popover.Target>
        <ActionIcon
          mx={4}
          variant="light"
          radius={selectedTrack ? 100 : "sm"}
          className={classes.button}
          mod={{ "saved-track": !!selectedTrack }}
          // The action; the tooltip says the track's state or why it can't be saved
          aria-label={t("editmode.save_indicator.tooltips.save")}
          onClick={() => {
            if (!selectedTrack) setIsSavePopoverOpen((prev) => !prev);
          }}
          disabled={
            !user ||
            !haveSaveableTrackwaypoints ||
            (!selectedTrack && isTrackLoading)
          }
        >
          <Tooltip label={getSaveTrackButtonLabel()}>
            <Center style={{ width: "100%", height: "100%" }}>
              {trackStatus === "saving" ? (
                <Loader size="xs" />
              ) : (
                <FontAwesomeIcon
                  icon={faSave}
                  color={
                    selectedTrack && trackStatus === "saved"
                      ? colorScheme === "dark"
                        ? theme.colors.teal[2]
                        : theme.colors.teal[6]
                      : colorScheme === "dark"
                        ? "inherit"
                        : "inherit"
                  }
                />
              )}
            </Center>
          </Tooltip>
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown p="xs">
        <div>
          <form onSubmit={(e) => void handleSaveTrackSubmit(e)}>
            <Stack gap={4}>
              <TextInput
                data-autofocus
                onChange={(e) => setNewTrackName(e.currentTarget.value)}
                size="xs"
                placeholder={t(
                  "editmode.save_indicator.name_input.placeholder"
                )}
              />
              <Button
                type="submit"
                color="teal"
                size="xs"
                loading={isTrackLoading}
              >
                {t("generic.save_button_label")}
              </Button>
            </Stack>
          </form>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
};
